import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import CopyRemoteSourceConfigModal from 'components/modals/copy-remote-source-config/CopyRemoteSourceConfigModal';
import { ChastiaConfig } from 'components/forms/remote-source/config/types';

/**
 * Parameters for opening the copy config modal.
 */
interface OpenCopyConfigParams {
  currentRemoteSourceId: number;
  onConfigSelected: (config: ChastiaConfig) => void;
}

/**
 * Context type.
 */
interface ICopyRemoteSourceConfigContext {
  openCopyConfigModal: (params: OpenCopyConfigParams) => void;
}

/**
 * The context.
 */
const CopyRemoteSourceConfigContext = createContext<ICopyRemoteSourceConfigContext>(undefined!);

/**
 * Provides the CopyRemoteSourceConfigContext.
 */
export default function CopyRemoteSourceConfigProvider({ children }: { children: React.ReactNode }) {
  const [opened, setOpened] = useState(false);
  const [params, setParams] = useState<OpenCopyConfigParams | null>(null);

  const openCopyConfigModal = useCallback((params: OpenCopyConfigParams) => {
    setParams(params);
    setOpened(true);
  }, []);

  const onClose = useCallback(() => {
    setOpened(false);
  }, []);

  const value = useMemo(() => ({ openCopyConfigModal }), [openCopyConfigModal]);

  return (
    <CopyRemoteSourceConfigContext.Provider value={value}>
      {children}
      {params && <CopyRemoteSourceConfigModal opened={opened} onClose={onClose} {...params} />}
    </CopyRemoteSourceConfigContext.Provider>
  );
}

/**
 * Hook to use the copy config modal.
 */
export function useCopyRemoteSourceConfig() {
  return useContext(CopyRemoteSourceConfigContext);
}
